import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Alert, Button, Message, Modal, Progress, Space, Table, Tag, Typography } from "@arco-design/web-react";

import { FetchApi, PostApi } from "../../Api/AuthenticatedApi";

const { Text } = Typography;

// Stripe's subscription statuses, in words.
const STATUS = {
  active: { label: "Active", color: "green" },
  trialing: { label: "Trial", color: "green" },
  comp: { label: "Custom", color: "green" },
  past_due: { label: "Payment failed", color: "red" },
  unpaid: { label: "Unpaid", color: "red" },
  canceled: { label: "Cancelled", color: "gray" },
  incomplete: { label: "Not finished", color: "orange" },
  incomplete_expired: { label: "Not finished", color: "gray" },
};

const fmtNumber = (n) => (n || 0).toLocaleString();
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "");
const fmtMonthly = (cents) => (cents
  ? "$" + (cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 }) + "/mo"
  : "Free");

// UsageRow shows one measure against what the plan includes. On a limited
// plan (free, or a deal) the included amount is a hard limit: at it, sending
// stops (atLimit says when). Otherwise extras are billed.
function UsageRow({ label, used, included, limited, atLimit }) {
  const over = included > 0 && used > included;
  const stopped = limited && included > 0 && atLimit(used, included);
  return (
    <div style={{ marginBottom: 12 }}>
      <Text>{label}: {fmtNumber(used)}{included > 0 ? ` of ${fmtNumber(included)} ${limited ? "allowed" : "included"}` : ""}</Text>
      {included > 0 && (
        <Progress percent={Math.min(100, Math.round((used / included) * 100))} status={over || stopped ? "warning" : "normal"} showText={false} />
      )}
      {stopped && <Text type="warning">Limit reached: sending is paused. Upgrade to keep sending.</Text>}
      {over && !limited && <Text type="warning">{fmtNumber(used - included)} over: charged as extra on the next bill.</Text>}
    </div>
  );
}

export default function BillingSettings() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [billing, setBilling] = useState(null);
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    FetchApi(getAccessTokenSilently, "/api/billing",
      (data) => setBilling(data),
      () => navigate("/login"),
      () => Message.error("Failed to load billing"));
  }, [getAccessTokenSilently, navigate]);

  // Coming back from Stripe.
  useEffect(() => {
    const checkout = new URLSearchParams(window.location.search).get("checkout");
    if (checkout === "success") Message.success("Thanks! Your plan shows here once Stripe confirms it, usually within a minute.");
    if (checkout === "cancel") Message.info("Checkout cancelled. Nothing was charged.");
  }, []);

  // goToStripe asks the API for a Stripe page and opens it.
  const goToStripe = (url, body) => {
    setBusy(true);
    PostApi(getAccessTokenSilently, url,
      (data) => { window.location.assign(data.url); },
      () => navigate("/login"),
      (err) => { setBusy(false); Message.error((err && err.message) || "Failed to open Stripe"); },
      body || {});
  };

  // changePlan moves a paying org to another plan, after asking.
  const changePlan = (p) => {
    Modal.confirm({
      title: `Switch to ${p.label || p.plan}?`,
      content: "The monthly fee changes now; you're charged or credited for the days left on your next bill. " +
        "Extra emails and contacts this period are charged at the new plan's rates.",
      onOk: () => new Promise((resolve) => {
        PostApi(getAccessTokenSilently, "/api/billing/change-plan",
          (data) => { setBilling(data); setChanging(false); Message.success("Plan changed"); resolve(); },
          () => navigate("/login"),
          (err) => { Message.error((err && err.message) || "Failed to change plan"); resolve(); },
          { plan: p.plan });
      }),
    });
  };

  if (!billing) return null;
  if (!billing.enabled) {
    return <Text type="secondary">Billing is off on this server. Every organization can send.</Text>;
  }

  const status = STATUS[billing.status];
  const plan = billing.plan;
  const limited = !!billing.limited;
  // Access given by our team outside Stripe: it sets the limits while it lasts.
  const grant = billing.grant;
  const paying = billing.status === "active" || billing.status === "trialing";
  // An org on a deal ("comp") or that never paid has no Stripe customer to manage.
  const hasCustomer = !!billing.status && billing.status !== "comp";
  // Only a plan that's being paid for can be switched; otherwise choose one.
  const canChange = !!plan && paying;
  // Plans to buy show for orgs that aren't paying, except those on a deal.
  const showPlans = changing || (!paying && billing.status !== "comp" && !grant);

  const planColumns = [
    { title: "Plan", dataIndex: "label", render: (l, p) => l || p.plan },
    { title: "Price", dataIndex: "monthly_cents", render: fmtMonthly },
    { title: "Contacts included", dataIndex: "included_contacts", render: fmtNumber },
    { title: "Emails included", dataIndex: "included_emails", render: fmtNumber },
    {
      title: "",
      render: (_, p) => {
        if (plan && p.plan === plan.plan) return <Tag color="green">Current</Tag>;
        if (!p.on_stripe) return null;
        if (canChange) return <Button type="primary" size="small" onClick={() => changePlan(p)}>Switch</Button>;
        return (
          <Button type="primary" size="small" loading={busy} onClick={() => goToStripe("/api/billing/checkout", { plan: p.plan })}>
            Choose
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      {!billing.can_send && (
        <Alert type="warning" style={{ marginBottom: 16 }} content={billing.blocked || "Sending is paused."} />
      )}

      <Space direction="vertical" style={{ width: "100%" }}>
        <Space>
          <Text bold>{plan ? (plan.label || plan.plan) : "No plan"}</Text>
          {grant ? (
            <Tag color="arcoblue">{grant.expires_at ? `Until ${fmtDate(grant.expires_at)}` : "No end date"}</Tag>
          ) : (
            <>
              {status && <Tag color={status.color}>{status.label}</Tag>}
              {plan && <Text type="secondary">{fmtMonthly(plan.monthly_cents)}</Text>}
            </>
          )}
        </Space>
        {grant && (
          <Text type="secondary">
            Our team set up this access for you, outside of card billing.
            {grant.expires_at ? " When it ends, normal billing applies again." : ""}
          </Text>
        )}

        <Text type="secondary">
          Usage from {fmtDate(billing.period_start)} to {fmtDate(billing.period_end)}
        </Text>
        {limited ? (
          <UsageRow label="Contacts stored" used={billing.contacts_now} included={plan ? plan.included_contacts : 0}
            limited atLimit={(used, max) => used > max} />
        ) : (
          <UsageRow label="Most contacts stored" used={billing.usage.contacts_peak} included={plan ? plan.included_contacts : 0} />
        )}
        <UsageRow label="Emails sent" used={billing.usage.emails_sent} included={plan ? plan.included_emails : 0}
          limited={limited} atLimit={(used, max) => used >= max} />
        <Text type="secondary">
          {limited
            ? "Contacts include unsubscribed ones. New contacts are always kept, even past the limit. The email limit resets on the 1st of each month. Test emails are free."
            : "Contacts are counted at their highest this period, including unsubscribed ones. Test emails are free."}
        </Text>

        <Space>
          {canChange && (
            <Button type={changing ? "secondary" : "primary"} onClick={() => setChanging(!changing)}>
              {changing ? "Keep current plan" : "Change plan"}
            </Button>
          )}
          {hasCustomer && (
            <Button loading={busy} onClick={() => goToStripe("/api/billing/portal")}>Manage billing</Button>
          )}
        </Space>

        {showPlans && (
          <Table rowKey="plan" columns={planColumns} data={billing.plans || []} pagination={false}
            noDataElement={<Text type="secondary">No plans are on sale yet.</Text>} />
        )}
      </Space>
    </div>
  );
}
